import React, { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/Card';
import { Calendar, MapPin, ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from './ui/Button';
import { getFullApiBaseUrl } from '../services/api'; // IMPORT ATUALIZADO

const EventCarousel = ({ events }) => {
    // Configuração para mostrar 1 slide por vez (hero section)
    const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: 'start' }, [Autoplay({ delay: 5000, stopOnInteraction: false })]);
    const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
    const [nextBtnDisabled, setNextBtnDisabled] = useState(true);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [scrollSnaps, setScrollSnaps] = useState([]);

    const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
    const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);
    const scrollTo = useCallback((index) => emblaApi && emblaApi.scrollTo(index), [emblaApi]); // NOVO: Função para rolar para um índice

    const onSelect = useCallback((emblaApi) => {
        if (!emblaApi) return;
        setSelectedIndex(emblaApi.selectedScrollSnap());
        setPrevBtnDisabled(!emblaApi.canScrollPrev());
        setNextBtnDisabled(!emblaApi.canScrollNext());
    }, []);

    useEffect(() => {
        if (!emblaApi) return;
        
        if (typeof emblaApi.scrollSnaps === 'function') {
            setScrollSnaps(emblaApi.scrollSnaps());
        }
        
        onSelect(emblaApi);
        
        emblaApi.on('select', onSelect);
        emblaApi.on('reInit', onSelect);
        
        return () => {
            emblaApi.off('select', onSelect);
            emblaApi.off('reInit', onSelect);
        };
    }, [emblaApi, onSelect]);

    if (!events || events.length === 0) return null;

    // Filtra apenas eventos futuros ou atuais que estão ativos.
    // A ordenação por data de início já é feita no componente pai (EventosPage).
    const featuredEvents = events.filter(event => new Date(event.dataFim) >= new Date() && event.ativo);

    if (featuredEvents.length === 0) return null;

    // Função para obter o URL completo da imagem
    const getFullImageUrl = (url) => {
        if (!url) return null;
        const baseUrl = getFullApiBaseUrl();
        
        // Remove barras iniciais do URL do evento
        let normalizedUrl = url.startsWith('/') ? url.substring(1) : url;
        
        // Adiciona 'files/' se não estiver presente
        if (!normalizedUrl.startsWith('files/')) {
            normalizedUrl = `files/${normalizedUrl}`;
        }
        
        // Concatena: http://localhost:8080/api/ + files/ + nome_do_arquivo
        return `${baseUrl}/${normalizedUrl}`;
    };

    return (
        <div className="relative mb-12"> 
            <h2 className="text-2xl font-bold tracking-tight mb-4 text-primary">Próximos Eventos</h2>
            
            {/* Container do Carrossel */}
            <div className="embla overflow-hidden rounded-xl shadow-lg" ref={emblaRef}>
                <div className="embla__container flex">
                    {featuredEvents.map((event, index) => {
                        const fullImageUrl = getFullImageUrl(event.imagemUrl);
                        const backgroundStyle = {
                            // Usa o placeholder se não houver URL de imagem
                            backgroundImage: `url(${fullImageUrl || '/placeholder-i.jpg'})` 
                        };

                        return (
                            // Slide ocupa 100% da largura
                            <div className="embla__slide flex-shrink-0 w-full" key={event.id}> 
                                <Link to={`/eventos/${event.id}`} className="block h-full">
                                    <div 
                                        className="relative w-full aspect-3508/1665 rounded-xl overflow-hidden shadow-2xl group bg-cover bg-center transition-all duration-500 ease-in-out hover:shadow-primary/50" // USANDO aspect-3508/1665
                                        // Define a imagem como background CSS
                                        style={backgroundStyle}
                                    >
                                        {/* Overlay para legibilidade do texto */}
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent flex flex-col justify-end p-6 transition-colors duration-300">
                                            <h3 className="text-xl md:text-2xl font-extrabold text-white mb-1 leading-tight"> {/* Reduzido o tamanho da fonte */}
                                                {event.nome}
                                            </h3>
                                            <p className="text-xs md:text-sm text-gray-200 line-clamp-1 mb-2"> {/* Reduzido o tamanho da fonte e line-clamp */}
                                                {/* Remove tags HTML da descrição para o destaque */}
                                                {event.descricao.replace(/<[^>]*>?/gm, '')}
                                            </p>
                                            
                                            {/* Detalhes */}
                                            <div className="flex flex-wrap items-center text-xs text-white/90 space-x-4"> {/* Reduzido o tamanho da fonte */}
                                                <div className="flex items-center">
                                                    <Calendar className="mr-2 h-3 w-3" /> {/* Reduzido o tamanho do ícone */}
                                                    <span>{new Date(event.dataInicio).toLocaleDateString()} - {new Date(event.dataFim).toLocaleDateString()}</span>
                                                </div>
                                                <div className="flex items-center">
                                                    <MapPin className="mr-2 h-3 w-3" /> {/* Reduzido o tamanho do ícone */}
                                                    <span>{event.local}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            </div>
                        );
                    })}
                </div>
            </div>
            
            {/* Botões de Navegação - Posicionados sobre o carrossel */}
            <Button 
                variant="outline" 
                size="icon" 
                onClick={scrollPrev} 
                disabled={prevBtnDisabled}
                // Posicionado 1rem (p-4) da borda esquerda
                className="absolute top-1/2 left-4 transform -translate-y-1/2 z-20 hidden lg:flex bg-background/80 hover:bg-background shadow-lg" 
            >
                <ArrowLeft className="h-4 w-4" />
            </Button>
            <Button 
                variant="outline" 
                size="icon" 
                onClick={scrollNext} 
                disabled={nextBtnDisabled}
                // Posicionado 1rem (p-4) da borda direita
                className="absolute top-1/2 right-4 transform -translate-y-1/2 z-20 hidden lg:flex bg-background/80 hover:bg-background shadow-lg" 
            >
                <ArrowRight className="h-4 w-4" />
            </Button>

            {/* NOVO: Dots/Pagination */}
            {featuredEvents.length > 1 && (
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 z-30">
                    {scrollSnaps.map((_, index) => (
                        <button
                            key={index}
                            className={`h-2 rounded-full transition-all duration-300 ${
                                index === selectedIndex ? 'bg-white w-6' : 'bg-white/50 hover:bg-white/80 w-2'
                            }`}
                            onClick={() => scrollTo(index)}
                            aria-label={`Ir para o slide ${index + 1}`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default EventCarousel;